import { useState, useEffect } from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  BookOpen,
  User,
  Calendar,
  FileText,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock3,
  FileQuestion,
  X,
  Download,
  Eye,
  Hash,
  Info,
  Tag,
} from "lucide-react";

import { downloadFile, getFileDetails } from "server/Files CRUD/Requests";
import { GetFileDetailsOutput } from "server/Files CRUD/OutputsTypes";
import FilesList from "components/common/FilesList";
import {
  getSubmissionTypeLabel,
  getSchoolClassLabel,
  getMatiereLabel,
} from "components/common/utils";

interface ConsultModalProps {
  open: boolean;
  onClose: () => void;
  submission: any; // Type de la soumission reçue
}

const getStatusInfo = (status: string) => {
  const statusMap: Record<
    string,
    { label: string; color: string; icon: React.ReactNode }
  > = {
    in_standby: {
      label: "En attente",
      color: "#FEF3C7",
      icon: <Clock3 className="w-4 h-4" />,
    },
    in_processing: {
      label: "En cours",
      color: "#DBEAFE",
      icon: <AlertCircle className="w-4 h-4" />,
    },
    resolved: {
      label: "Terminée",
      color: "#D1FAE5",
      icon: <CheckCircle2 className="w-4 h-4" />,
    },
    rejected: {
      label: "Rejetée",
      color: "#FEE2E2",
      icon: <XCircle className="w-4 h-4" />,
    },
  };
  return (
    statusMap[status] || {
      label: "Inconnu",
      color: "#F3F4F6",
      icon: <FileQuestion className="w-4 h-4" />,
    }
  );
};

const getReferenceTypeLabel = (referenceType: string) => {
  const typeMap: Record<string, string> = {
    livre: "Livre",
    epreuve: "Épreuve",
    other: "Autre",
  };
  return typeMap[referenceType] || referenceType;
};

const formatFileSize = (bytes: number, decimalPoint = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimalPoint < 0 ? 0 : decimalPoint;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const ConsultModal: React.FC<ConsultModalProps> = ({
  open,
  onClose,
  submission,
}) => {
  const [subjectFiles, setSubjectFiles] = useState<GetFileDetailsOutput[]>([]);
  const [productionFiles, setProductionFiles] = useState<
    GetFileDetailsOutput[]
  >([]);
  const [filesLoading, setFilesLoading] = useState(true);

  useEffect(() => {
    if (open && submission) {
      loadFilesMetadata();
    }
  }, [open, submission]);

  const loadFilesMetadata = async () => {
    try {
      setFilesLoading(true);

      // Charger les fichiers du sujet
      if (submission.subjectFileIds && submission.subjectFileIds.length > 0) {
        const subjectFilesData = await Promise.allSettled(
          submission.subjectFileIds.map(async (fileId: string) => {
            try {
              const response = await getFileDetails(fileId);
              if (response.status === 200) {
                return response.body as GetFileDetailsOutput;
              }
              return null;
            } catch (error) {
              console.error(`Erreur sur le fichier ${fileId}:`, error);
              return null;
            }
          }),
        );

        const validSubjectFiles = subjectFilesData
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<GetFileDetailsOutput | null> =>
              result.status === "fulfilled" && result.value !== null,
          )
          .map((result) => result.value!);

        setSubjectFiles(validSubjectFiles);
      }

      // Charger les fichiers de production
      if (
        submission.productionFileIds &&
        submission.productionFileIds.length > 0
      ) {
        const productionFilesData = await Promise.allSettled(
          submission.productionFileIds.map(async (fileId: string) => {
            try {
              const response = await getFileDetails(fileId);
              if (response.status === 200) {
                return response.body as GetFileDetailsOutput;
              }
              return null;
            } catch (error) {
              console.error(`Erreur sur le fichier ${fileId}:`, error);
              return null;
            }
          }),
        );

        const validProductionFiles = productionFilesData
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<GetFileDetailsOutput | null> =>
              result.status === "fulfilled" && result.value !== null,
          )
          .map((result) => result.value!);

        setProductionFiles(validProductionFiles);
      }
    } catch (error) {
      console.error("Erreur générale lors du chargement des fichiers:", error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleFileDownload = async (fileId: string, filename: string) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier:", error);
    }
  };

  // Si pas de soumission, ne pas afficher le modal
  if (!submission) {
    return null;
  }

  const statusInfo = getStatusInfo(submission.status);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      {/* Header */}
      <div className="relative p-6 bg-[#E3B500] text-white flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <FileText className="w-6 h-6" />
          <h2 className="text-2xl font-bold">Détails de la soumission</h2>
        </div>
        <IconButton onClick={onClose} className="text-white">
          <X className="w-6 h-6" />
        </IconButton>
      </div>

      {/* Contenu */}
      <div className="p-4 md:p-6 max-h-[calc(100vh-200px)] overflow-auto">
        {/* Section principale */}
        <div className="space-y-6 md:space-y-8">
          {/* En-tête avec informations principales */}
          <div className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Matière */}
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">
                    Matière
                  </p>
                  <p className="text-base md:text-lg font-semibold text-gray-800">
                    {getMatiereLabel(submission.matiere)}
                  </p>
                </div>
              </div>

              {/* Type de soumission */}
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">
                    Type
                  </p>
                  <p className="text-base md:text-lg font-semibold text-gray-800">
                    {submission.submissionType
                      ? getSubmissionTypeLabel(submission.submissionType)
                      : "Non disponible"}
                  </p>
                </div>
              </div>

              {/* Statut */}
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium">
                    Statut
                  </p>
                  <span
                    className="inline-flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium"
                    style={{ backgroundColor: statusInfo.color }}
                  >
                    {statusInfo.icon}
                    {statusInfo.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Informations de l'étudiant */}
          {/* <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-800">Informations de l'étudiant</h3>
            </div>
            
            <div className="ml-8 md:ml-9 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">Nom complet</p>
                  <p className="text-sm md:text-base text-gray-800">
                    {submission.student && submission.student.firstname && submission.student.lastname
                      ? `${submission.student.firstname} ${submission.student.lastname}`
                      : "Non disponible"
                    }
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">Matricule</p>
                  <p className="text-sm md:text-base text-gray-800">
                    {submission.student?.matricule || "Non disponible"}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">École</p>
                  <p className="text-sm md:text-base text-gray-800 break-words">
                    {submission.student?.schoolName || "Non disponible"}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">Classe</p>
                  <p className="text-sm md:text-base text-gray-800">
                    {submission.student?.schoolClass
                      ? getSchoolClassLabel(submission.student.schoolClass)
                      : "Non disponible"
                    }
                  </p>
                </div>
              </div>
            </div>
          </div> */}

          {/* Référence */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <Tag className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                Référence
              </h3>
            </div>

            <div className="ml-8 md:ml-9 space-y-4">
              {/* Type de référence */}
              <div>
                <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                  Type de référence
                </p>
                <p className="text-base md:text-lg text-gray-800">
                  {submission.referenceType
                    ? getReferenceTypeLabel(submission.referenceType)
                    : "Non disponible"}
                </p>
              </div>

              {/* Détails de la référence */}
              {submission.referenceBook ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Livre
                    </p>
                    <p className="text-sm md:text-base text-gray-800 break-words">
                      {submission.referenceBook.name || "Non disponible"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Page
                    </p>
                    <p className="text-sm md:text-base text-gray-800">
                      {submission.referenceBook.page || "Non disponible"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Exercice
                    </p>
                    <p className="text-sm md:text-base text-gray-800">
                      {submission.referenceBook.exerciseNumber ||
                        "Non disponible"}
                    </p>
                  </div>
                </div>
              ) : submission.referenceTest ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      École
                    </p>
                    <p className="text-sm md:text-base text-gray-800 break-words">
                      {submission.referenceTest.schoolName || "Non disponible"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Année
                    </p>
                    <p className="text-sm md:text-base text-gray-800">
                      {submission.referenceTest.year || "Non disponible"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Session
                    </p>
                    <p className="text-sm md:text-base text-gray-800 break-words">
                      {submission.referenceTest.session || "Non disponible"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                      Classe
                    </p>
                    <p className="text-sm md:text-base text-gray-800">
                      {submission.referenceTest.class
                        ? getSchoolClassLabel(submission.referenceTest.class)
                        : "Non disponible"}
                    </p>
                  </div>
                </div>
              ) : submission.referenceOther ? (
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                    Information
                  </p>
                  <p className="text-sm md:text-base text-gray-800 break-words">
                    {submission.referenceOther.info || "Non disponible"}
                  </p>
                </div>
              ) : (
                <p className="text-sm md:text-base text-gray-500 italic">
                  Aucune référence disponible
                </p>
              )}
            </div>
          </div>

          {/* Fichiers */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                Fichiers
              </h3>
            </div>

            <div className="ml-8 md:ml-9 space-y-6">
              {/* Fichiers du sujet */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h4 className="text-base md:text-lg font-medium text-gray-700">
                    Fichiers du sujet
                  </h4>
                  <span className="text-xs md:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded self-start sm:self-auto">
                    {submission.subjectFileIds?.length || 0} fichier(s)
                  </span>
                </div>
                {subjectFiles.length > 0 ? (
                  <div className="space-y-2">
                    {subjectFiles.map((file) => (
                      <div
                        key={file._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <FileText className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-sm md:text-base text-gray-700 font-medium truncate"
                              title={file.originalName}
                            >
                              {file.originalName}
                            </p>
                            <p className="text-xs md:text-sm text-gray-500 truncate">
                              {file.mimeType} • {formatFileSize(file.size || 0)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleFileDownload(file._id, file.originalName)
                          }
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm md:text-base text-gray-500 italic text-center py-4">
                    Aucun fichier de sujet disponible
                  </p>
                )}
              </div>

              {/* Fichiers de production */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h4 className="text-base md:text-lg font-medium text-gray-700">
                    Fichiers de production
                  </h4>
                  <span className="text-xs md:text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded self-start sm:self-auto">
                    {submission.productionFileIds?.length || 0} fichier(s)
                  </span>
                </div>
                {productionFiles.length > 0 ? (
                  <div className="space-y-2">
                    {productionFiles.map((file) => (
                      <div
                        key={file._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <FileText className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-sm md:text-base text-gray-700 font-medium truncate"
                              title={file.originalName}
                            >
                              {file.originalName}
                            </p>
                            <p className="text-xs md:text-sm text-gray-500 truncate">
                              {file.mimeType} • {formatFileSize(file.size || 0)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleFileDownload(file._id, file.originalName)
                          }
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm md:text-base text-gray-500 italic text-center py-4">
                    Aucun fichier de production disponible
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Commentaires et observations */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                Commentaires
              </h3>
            </div>

            <div className="ml-8 md:ml-9 space-y-4">
              {/* Commentaire principal */}
              <div>
                <p className="text-xs md:text-sm text-gray-500 font-medium mb-2">
                  Commentaire
                </p>
                <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm md:text-base text-gray-800 break-words">
                    {submission.comment || "Aucun commentaire"}
                  </p>
                </div>
              </div>

              {/* Observation */}
              {submission.observation && (
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-2">
                    Observation
                  </p>
                  <div className="p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm md:text-base text-gray-800 break-words">
                      {submission.observation}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informations complémentaires */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Dates */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  Dates
                </h3>
              </div>

              <div className="ml-8 md:ml-9 space-y-3">
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                    Créé le
                  </p>
                  <p className="text-sm md:text-base text-gray-800">
                    {submission.createdAt
                      ? new Date(submission.createdAt).toLocaleDateString(
                          "fr-FR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "Non disponible"}
                  </p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-500 font-medium mb-1">
                    Mis à jour le
                  </p>
                  <p className="text-sm md:text-base text-gray-800">
                    {submission.updatedAt
                      ? new Date(submission.updatedAt).toLocaleDateString(
                          "fr-FR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "Non disponible"}
                  </p>
                </div>
              </div>
            </div>

            {/* État */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <Info className="w-5 h-5 md:w-6 md:h-6 text-[#E3B500]" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  État
                </h3>
              </div>

              <div className="ml-8 md:ml-9">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      submission.isActivated ? "bg-green-500" : "bg-red-500"
                    }`}
                  ></div>
                  <span className="text-sm md:text-base text-gray-800 font-medium">
                    {submission.isActivated ? "Activé" : "Non activé"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 bg-gray-50 border-t flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-[#E3B500] text-white rounded-lg flex items-center gap-2 hover:bg-[#CA8A04] transition-colors"
        >
          <X className="w-4 h-4" />
          Fermer
        </button>
      </div>
    </Dialog>
  );
};

export default ConsultModal;
